import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Check, CalendarClock } from 'lucide-react'
import { SHIFT_TYPES, shiftDef, shiftHours, formatHours, shiftSummary, type ShiftCode, type ShiftPlanEntryDto } from '@familynest/shared'
import { api } from '../../lib/api'
import { useLive } from '../../lib/ws'
import { addMonths, startOfMonth, startOfWeek, addDays, formatMonthYear } from '../../lib/date'
import { Button } from '../ui'
import { useDialogs } from '../ui/dialogs'

/** Farbe je Schicht (klar unterscheidbar, Fable-Vorschlag). */
const SHIFT_COLOR: Record<ShiftCode, string> = {
  FD_T: '#2e6fb0', // blau
  LFD_T: '#1e3a5f', // dunkles Marineblau (langer Frühdienst, Blau-Familie)
  SD_T: '#d98324', // orange
  FSD_T: '#7d5ba6', // violett
  FD_C: '#3f8f4f', // grün
  SD_C: '#c0563f', // terracotta
  TU: '#3f8f8f', // teal (Urlaub)
  K: '#9b3d6e', // plum (krank)
}
const WD = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const pad = (n: number): string => String(n).padStart(2, '0')
const dayKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const monthKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`

/**
 * Dienstplan (ADR-041, nur Eltern): Monats-Kalenderblatt; pro Tag per Klick eine
 * Schicht wählen. Unten Stunden-Summe + „In den Kalender übernehmen" (erzeugt
 * Termine des pflegenden Users mit 2-h-Erinnerung; alle anderen sehen sie nur).
 */
export function ShiftPlanScreen(): React.JSX.Element {
  const dialogs = useDialogs()
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()))
  const [entries, setEntries] = useState<ShiftPlanEntryDto[]>([])
  const [picker, setPicker] = useState<string | null>(null) // Tag (YYYY-MM-DD) im Auswahl-Sheet
  const [applying, setApplying] = useState(false)

  const month = monthKey(cursor)
  const load = useCallback(async () => setEntries(await api.listShiftPlan(month)), [month])
  useEffect(() => {
    void load()
  }, [load])
  useLive('calendar', load)

  const byDate = useMemo(() => new Map(entries.map((e) => [e.date, e])), [entries])
  const gridStart = startOfWeek(startOfMonth(cursor))
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
  const sum = shiftSummary(entries.map((e) => e.code))
  const appliedCount = entries.filter((e) => e.eventId).length

  async function pick(date: string, code: ShiftCode | null): Promise<void> {
    setPicker(null)
    await api.setShift(date, code)
    await load()
  }
  async function apply(): Promise<void> {
    if (!(await dialogs.confirm({ title: 'In Kalender übernehmen', message: `${entries.length} Dienst(e) für ${formatMonthYear(cursor)} in den Familienkalender übernehmen? Du wirst jeweils 2 h vorher erinnert.`, confirmLabel: 'Übernehmen' }))) return
    setApplying(true)
    try {
      const r = await api.applyShiftPlan(month)
      await load()
      await dialogs.alert({ title: 'Übernommen', message: `${r.created} neu, ${r.updated} aktualisiert.` })
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 p-3 md:p-4">
      {/* Monatswahl */}
      <div className="flex items-center gap-1">
        <button onClick={() => setCursor((c) => addMonths(c, -1))} aria-label="Vorheriger Monat" className="rounded-lg p-2 hover:bg-black/5"><ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} /></button>
        <button onClick={() => setCursor((c) => addMonths(c, 1))} aria-label="Nächster Monat" className="rounded-lg p-2 hover:bg-black/5"><ChevronRight className="size-5" style={{ color: 'var(--color-ink)' }} /></button>
        <h1 className="ml-1 flex items-center gap-2 text-lg font-semibold capitalize" style={{ color: 'var(--color-ink)' }}>
          <CalendarClock className="size-5" style={{ color: 'var(--color-accent)' }} /> {formatMonthYear(cursor)}
        </h1>
        <Button variant="ghost" className="ml-auto" onClick={() => setCursor(startOfMonth(new Date()))}>Heute</Button>
      </div>

      {/* Kalenderblatt */}
      <div className="grid grid-cols-7 gap-1">
        {WD.map((w) => (
          <div key={w} className="py-1 text-center text-xs font-medium" style={{ color: 'var(--color-muted)' }}>{w}</div>
        ))}
        {days.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth()
          const entry = byDate.get(dayKey(d))
          return (
            <button
              key={dayKey(d)}
              onClick={() => inMonth && setPicker(dayKey(d))}
              disabled={!inMonth}
              className="flex aspect-square flex-col items-center justify-start gap-1 rounded-lg border p-1 text-left disabled:opacity-30"
              style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}
            >
              <span className="self-end text-xs" style={{ color: 'var(--color-muted)' }}>{d.getDate()}</span>
              {entry && (
                <span className="flex w-full items-center justify-center rounded px-0.5 py-0.5 text-[10px] font-semibold leading-tight text-white" style={{ backgroundColor: SHIFT_COLOR[entry.code] }}>
                  {shiftDef(entry.code).label}
                  {entry.eventId && <Check className="ml-0.5 size-2.5" />}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Legende */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 rounded-lg border p-2 text-xs" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)', color: 'var(--color-muted)' }}>
        {SHIFT_TYPES.map((s) => (
          <span key={s.code} className="flex items-center gap-1">
            <span className="size-3 rounded-sm" style={{ backgroundColor: SHIFT_COLOR[s.code] }} />
            {s.absence ? `${s.label} · ${s.title} (ganztägig)` : `${s.label} · ${s.title} · ${s.start}–${s.end} · ${formatHours(shiftHours(s.code))}`}
          </span>
        ))}
      </div>

      {/* Summe + Übernahme */}
      <div className="sticky bottom-0 flex items-center gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <div className="flex-1">
          <div className="font-semibold capitalize" style={{ color: 'var(--color-ink)' }}>Summe {formatMonthYear(cursor)}: {formatHours(sum.hours)}</div>
          <div className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {sum.dienste} Dienste{sum.urlaub ? ` · ${sum.urlaub} Urlaub` : ''}{sum.krank ? ` · ${sum.krank} Krank` : ''}
            {appliedCount < entries.length ? ` · ${entries.length - appliedCount} noch nicht übernommen` : ''}
          </div>
        </div>
        <Button onClick={apply} disabled={applying || entries.length === 0}>In den Kalender übernehmen</Button>
      </div>

      {/* Auswahl-Sheet */}
      {picker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setPicker(null)}>
          <div className="w-full max-w-sm rounded-t-2xl border p-4 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-3 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>
              {new Date(picker + 'T00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h2>
            <div className="flex flex-col gap-2">
              {SHIFT_TYPES.map((s) => {
                const active = byDate.get(picker)?.code === s.code
                return (
                  <button key={s.code} onClick={() => void pick(picker, s.code)} className="flex items-center gap-3 rounded-lg border px-3 py-2 text-left" style={{ borderColor: active ? SHIFT_COLOR[s.code] : 'var(--color-line)', backgroundColor: active ? SHIFT_COLOR[s.code] + '18' : 'var(--color-surface)' }}>
                    <span className="size-4 rounded-sm" style={{ backgroundColor: SHIFT_COLOR[s.code] }} />
                    <span className="flex-1" style={{ color: 'var(--color-ink)' }}><b>{s.label}</b> {s.title} <span style={{ color: 'var(--color-muted)' }}>{s.absence ? '(ganztägig)' : `(${s.start}–${s.end})`}</span></span>
                    <span className="text-sm" style={{ color: 'var(--color-muted)' }}>{s.absence ? '' : formatHours(shiftHours(s.code))}</span>
                  </button>
                )
              })}
              <button onClick={() => void pick(picker, null)} className="rounded-lg border px-3 py-2 text-left text-sm" style={{ borderColor: 'var(--color-line)', color: 'var(--color-danger)' }}>Kein Dienst (Tag leeren)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
